import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CardContent,
  Grid,
  Typography,
  Button,
  FormControl,
  Toolbar,
  MenuItem,
  CircularProgress,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Popover,
  TextField,
  IconButton,
  Collapse,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'jspdf-autotable';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import timezone from 'dayjs/plugin/timezone'; // Import timezone plugin
import utc from 'dayjs/plugin/utc'; // Import utc plugin to handle UTC
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
dayjs.extend(isSameOrAfter);



// Replace:
const exportBrokerDataToExcel = (brokerRows, getCommissionRate, toNumber, clients) => {
  const data = [];

  brokerRows.forEach(broker => {
    const brokerRow = {
      'Broker ID': broker.brokerId,
      'Broker Name': broker.brokerName,
      'Total Volumes': broker.totalVolumes,
      'Total Commission Volumes': broker.totalCommissionVolumes,
      'Earned Commission': broker.earnedCommission,
    };
    data.push(brokerRow);

    if (broker.clients && broker.clients.length > 0) {
      broker.clients.forEach(client => {
        const diesel = toNumber(client.totalDiesel);
        const rfr = toNumber(client.totalRfr);
        const vol = diesel + rfr;
        // Modified: Pass invoiceEndDate, clients, and toNumber to getCommissionRate
        const rate = getCommissionRate(client.clientId, client.invoiceEndDate, clients, toNumber);
        const commission = vol * rate;

        data.push({
          'Broker ID': '',
          'Broker Name': `↳ ${client.customerName}`,
          'Invoice Period': client.invoiceId,
          'Total Volumes': vol,
          'Total Commission Volumes': vol,
          'Earned Commission': commission,
        });
      });
    }
  });

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Broker Report');

  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  const fileData = new Blob([excelBuffer], {
    type: 'application/octet-stream',
  });

  saveAs(fileData, 'broker_report.xlsx');
};


// Place this after imports and exportBrokerDataToExcel (around line 80)
const getCommissionRate = (clientId, invoiceEndDate, clients, toNumber) => {
  if (!Array.isArray(clients)) return 0; // Return 0 if clients is not an array
  const client = clients.find(c => String(c.clientID) === String(clientId));
  if (!client || !client.brokerCommissionHistoryInternal) return 0;

  const history = client.brokerCommissionHistoryInternal;
  const invoiceDate = dayjs(invoiceEndDate);

  // Sort history dates in descending order to find the most recent applicable rate
  const sortedDates = Object.keys(history).sort((a, b) => dayjs(b).diff(dayjs(a)));

  for (const date of sortedDates) {
    const historyDate = dayjs(history[date].lastUpdatedDate);
    if (invoiceDate.isSameOrAfter(historyDate, 'day')) {
      const rateRaw = toNumber(history[date].commission);
      return rateRaw > 1 ? rateRaw / 100 : rateRaw;
    }
  }

  // Default to 0 if no applicable rate is found
  return 0;
};

function TableToolbar({ onReset, onExport }) {
  return (
    <div style={{ width: '15%', margin: '10px 0', marginLeft: '800px' }}>
      <Toolbar
        variant="dense"
        sx={{
          minHeight: 36,
          pl: 1,
          pr: 1,
          backgroundColor: '#f5f5f5',
          border: '1px solid #ddd',
          borderRadius: '5px',
        }}
      >
        <Button size="small" startIcon={<DownloadIcon />} onClick={onExport} sx={{ fontSize: 12, textTransform: 'uppercase', ml: 1 }}>
          Export
        </Button>
      </Toolbar>
    </div>
  );
}

function BrokerReport() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const buttonBackground = '#011f4b';
  const [clients, setClients] = React.useState([]);

  const [brokers, setBrokers] = useState([]);
  const [, setCommissionLookup] = React.useState({});
  const [, setClientsCAD] = React.useState([]);
  const [, setClientsUSD] = React.useState([]);
  const [, setCustomerId] = React.useState('');

  const [clientId, setClientId] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const [customerErrorText, setCustomerErrorText] = useState('');
  const [fetchedWithNoRecords, setFetchedWithNoRecords] = React.useState(false);
  const [, setCardsForClient] = React.useState([]);
  const doNotPerformCustomerFiltration = localStorage.getItem('role') === 'Super Admin' || localStorage.getItem('role') === 'Employee';
  const loggedInUserClientId = localStorage.getItem('clientId');
  const [clientDetails, setClientDetails] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('CAD'); // New state to store the selected currency
  const [, setRows] = React.useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);

  const [invoices, setInvoices] = useState([]);

  const [startDate, setStartDate] = React.useState(null);

  const [startDateErrorText, setStartDateErrorText] = React.useState('');

  const [endDate, setEndDate] = React.useState(null);
  const [error, setError] = React.useState('');
  const [endDateErrorText, setEndDateErrorText] = React.useState('');

  const [expandedRowIds, setExpandedRowIds] = useState([]);

  const loggedInUserBrokerID = localStorage.getItem('brokerID');

  const toggleRowExpansion = id => {
    setExpandedRowIds(prev => (prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]));
  };

  const inputLabelStyles = { fontSize: '14px', color: 'rgb(26, 33, 56)' };
  const textFieldLabelStyles = {
    style: {
      fontSize: '14px',
      color: 'rgb(26, 33, 56)',
      fontFamily: 'DM Sans, sans-serif',
    },
  };
  const gridRowStyles = { marginTop: '5px', marginBottom: '5px' };

useEffect(() => {
  if (!localStorage.getItem('role') === 'Broker' && loggedInUserBrokerID) {
    setSelectedBroker(loggedInUserBrokerID);
  }
}, [loggedInUserBrokerID, doNotPerformCustomerFiltration]);

const fetchBrokers = async () => {
  setLoading(true);
  setError(null);
  try {
    const response = await fetch('https://wex-backend.hub-ksgfuel.com/v1/user');
    if (!response.ok) {
      const errorMessage = await response.text();
      throw new Error(`Error fetching users: ${response.status} ${response.statusText} - ${errorMessage}`);
    }
    const data = await response.json();

    // Filter users with role: "Broker"
    const brokerUsers = data.filter(user => user.role === 'Broker');

    // Map to brokers format: { id, brokerName }
    const brokersData = brokerUsers.map(user => ({
      id: user.brokerID,
      brokerName: user.name || user.userName, // Use name if available, else userName
    }));

    // Apply filtration for Broker role
    const filteredBrokers = doNotPerformCustomerFiltration
      ? brokersData
      : brokersData.filter(broker => String(broker.id) === String(loggedInUserBrokerID));


   const sorted = filteredBrokers.sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10));

    setBrokers(sorted);
  } catch (error) {
    console.error('Error fetching brokers:', error);
    setError(error.message);
  } finally {
    setLoading(false);
  }
};

  const fetchBrokerClients = async brokerId => {
    console.log('Broker id: ', brokerId);
    try {
      const response = await fetch(`${BACKEND_URL}/v1/broker/${brokerId}/clients`);
      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(`Error fetching broker clients: ${response.status} ${response.statusText} - ${errorMessage}`);
      }
      const brokerClients = await response.json();
      // setBrokerClientData(brokerClients);

      // Set full client data
      // setClients(brokerClients);

      // Set selectedClients to their IDs
      // const selectedClientIDs = brokerClients.map(client => client.clientID);
      // setSelectedClients(selectedClientIDs);

      // return brokerClients.length === 0 ? [] : brokerClients;

      // add brokerId field so we know who owns this client
      const tagged = brokerClients.map(c => ({ ...c, brokerId }));
      // keep a master list
      setClients(prev => [...prev, ...tagged]);

      // Set selectedClients to their IDs
      const selectedClientIDs = tagged.map(client => client.clientID);
      setSelectedClients(selectedClientIDs);

      return tagged;
    } catch (error) {
      console.error('Error fetching broker clients:', error);
      alert('An error occurred while fetching broker clients.');
      // setSnackbarOpen(true);
      throw error;
    }
  };

  useEffect(() => {
    fetchBrokers();
  }, [loggedInUserClientId]);

  // Use Effect to handle single client case
  useEffect(() => {
    if (clients.length === 1) {
      const singleClient = clients[0];
      const singleClientValue = `${singleClient.clientID}-${singleClient.customerID}`;
      setClientId(singleClient.clientID);
      setCustomerId(singleClient.customerID);

      // Trigger handleSelectChange programmatically
      handleSelectChange({ target: { value: singleClientValue } });
    }
  }, [clients]); // Only depends on clients array

  const handleSelectChange = event => {
    setCustomerId(event.target.value);

    handleCustomerSelectionChange(event);
  };

  const handleCustomerSelectionChange = async (clientID, customerID) => {
    setLoading(true);
    setRows([]);
    console.log('Client inside handlecustomer ', clientID);
    try {
      // Use clientID in the getClientsUrl
      const getClientsUrl = `${BACKEND_URL}/v1/client?clientID=${clientID}`;
      const getCardDetailsForCustomerUrl = `${BACKEND_URL}/v1/cards?customerId=${customerID}&maxRows=1000&pageNumber=1000`;

      const [clientResponse, cardResponse] = await Promise.all([fetch(getClientsUrl), fetch(getCardDetailsForCustomerUrl)]);

      if (!clientResponse.ok) {
        throw new Error('Failed to fetch client data');
      }

      if (!cardResponse.ok) {
        throw new Error('Failed to fetch card details');
      }

      const clientData = await clientResponse.json();
      const cardData = await cardResponse.json();

      setSelectedClient(clientData);
      console.log('Data client', cardData?.records);
      setCardsForClient(cardData?.records?.card);
      console.log('Customer Details new', clientData);
    } catch (error) {
      console.error('Error fetching client or card details:', error.message);
    } finally {
      setLoading(false);
    }
  };

  // Set the customerId to the first item if only one item is present
  useEffect(() => {
    if (!doNotPerformCustomerFiltration && clients.length === 1) {
      const firstClient = clients[0];
      setCustomerId(firstClient.customerID);
      handleCustomerSelectionChange({
        target: { value: firstClient.customerID },
      });
    }
  }, [doNotPerformCustomerFiltration, loggedInUserClientId, BACKEND_URL]);

  const effectiveDateMap = React.useMemo(() => Object.fromEntries(clients.filter(c => c.effectiveDate).map(c => [String(c.clientID), dayjs(c.effectiveDate).format('YYYY-MM-DD')])), [clients]);

  const getInvoicesByClient = async clientId => {
    const url = `${BACKEND_URL}/v1/get-sales-report-by-client?clientId=${clientId}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Inv API error ${res.status}`);
    return res.json(); // returns an array
  };

  /* helper — clean + add start/end date fields */
  const normaliseInvoices = data =>
    data.map(inv => {
      const parts = inv.invoiceId.split('_'); // 110_Feb272025_Mar022025
      if (parts.length === 3) {
        const [, startStr, endStr] = parts;
        return {
          ...inv,
          invoiceStartDate: dayjs(startStr, 'MMMDDYYYY').format('YYYY-MM-DD'),
          invoiceEndDate: dayjs(endStr, 'MMMDDYYYY').format('YYYY-MM-DD'),
        };
      }
      return inv;
    });

  const fetchInvoices = async event => {
    event?.preventDefault();
    setUploading(true);

    /* 1️⃣ validate date range ------------------------------------------------ */
    if (startDate && endDate && dayjs(startDate).isAfter(dayjs(endDate))) {
      alert('Start date cannot be after end date');
      setUploading(false);
      return;
    }

    try {
      let allInvoices = [];

      /* 2️⃣ selector precedence --------------------------------------------- */
      /* ▸▸ 2a. explicit single‑client takes priority (legacy flow) */
      if (selectedClient && selectedClient !== 'all') {
        const data = await getInvoicesByClient(selectedClient);
        allInvoices = normaliseInvoices(data);

        /* ▸▸ 2b. single broker selected */
      } else if (selectedBroker && selectedBroker !== 'all') {
        const brokerClients = await fetchBrokerClients(selectedBroker); // returns array of client objects
        const clientIds = brokerClients.map(c => c.clientID);

        const invoicesArrays = await Promise.all(clientIds.map(id => getInvoicesByClient(id)));
        allInvoices = normaliseInvoices(invoicesArrays.flat());

        /* ▸▸ 2c. ALL brokers */
      } else {
        // make sure brokers are loaded
        if (!Array.isArray(brokers) || brokers.length === 0) {
          await fetchBrokers(); // fills `brokers` state
        }

        /* fetch every broker’s clients */
        const allClientsArrays = await Promise.all(brokers.map(b => fetchBrokerClients(b.id)));
        const clientIds = allClientsArrays.flat().map(c => c.clientID);

        /* fetch every client’s invoices */
        const invoicesArrays = await Promise.all(clientIds.map(id => getInvoicesByClient(id)));
        allInvoices = normaliseInvoices(invoicesArrays.flat());
      }

      /* 🔄  Combine effective‑date filter + optional date‑range filter */
      allInvoices = allInvoices
        // 1️⃣ filter out invoices that end before the client’s effectiveDate
        .filter(inv => {
          const key = String(inv.clientId ?? inv.clientID); // handles both casings
          const eff = effectiveDateMap[key]; // YYYY‑MM‑DD or undefined
          return !eff || inv.invoiceEndDate >= eff; // keep if no rule OR end ≥ effective
        })
        // 2️⃣ filter by user‑selected start / end range (if any)
        .filter(({ invoiceStartDate, invoiceEndDate }) => {
          const s = startDate ? dayjs(startDate).format('YYYY-MM-DD') : null;
          const e = endDate ? dayjs(endDate).format('YYYY-MM-DD') : null;

          if (s && e) return invoiceEndDate >= s && invoiceStartDate <= e;
          if (s) return invoiceEndDate >= s;
          if (e) return invoiceStartDate <= e;
          return true;
        });

      /* 4️⃣ push to state --------------------------------------------------- */
      setFetchedWithNoRecords(allInvoices.length === 0);
      setInvoices(allInvoices);
    } catch (err) {
      console.error('Error fetching invoice details:', err);
      alert('Failed to fetch invoices. Check console for details.');
    } finally {
      setUploading(false);
    }
  };

  // const toNumber = (v) => {
  //   if (v === null || v === undefined) return 0;
  //   const num = parseFloat(String(v).replace(/,/g, ''));
  //   return Number.isNaN(num) ? 0 : num;
  // };

  // number coercion
  const toNumber = v => {
    if (v == null) return 0;
    const n = parseFloat(String(v).replace(/,/g, ''));
    return Number.isNaN(n) ? 0 : n;
  };



  /* one‑time memo: clientID -> { brokerId, brokerName } */
  const clientBrokerMap = React.useMemo(() => {
    const map = {};
    brokers.forEach(b => {
      const brokerClients = clients.filter(c => c.brokerId === b.id); // tagged above
      brokerClients.forEach(c => {
        map[c.clientID] = { brokerId: b.id, brokerName: b.brokerName };
      });
    });
    return map;
  }, [brokers, clients]);

  // Replace the commission calculation in the `brokerRows` memo:
  const brokerRows = React.useMemo(() => {
    if (!Array.isArray(invoices) || !invoices.length) return [];

    /* 1️⃣ filter invoices by effective date first */
    const filteredInv = invoices.filter(inv => {
      const key = String(inv.clientId ?? inv.clientID);
      const eff = effectiveDateMap[key];
      return !eff || inv.invoiceEndDate >= eff;
    });

    /* 2️⃣ group per broker ------------------------------------ */
    const grouped = {};

    filteredInv.forEach(inv => {
      const owner = clientBrokerMap[inv.clientId ?? inv.clientID];
      if (!owner) return;

      const { brokerId, brokerName } = owner;
      if (!grouped[brokerId]) grouped[brokerId] = { id: brokerId, brokerId, brokerName, clients: [] };
      grouped[brokerId].clients.push(inv);
    });

    /* 3️⃣ compute totals -------------------------------------- */
    return Object.values(grouped).map(g => {
      const totals = g.clients.reduce(
        (acc, c) => {
          const vol = toNumber(c.totalDiesel) + toNumber(c.totalRfr);
          // Modified: Use getCommissionRate instead of commissionLookup
          const rate = getCommissionRate(c.clientId, c.invoiceEndDate, clients, toNumber);
          acc.totalVolume += vol;
          acc.earnedComm += vol * rate;
          return acc;
        },
        { totalVolume: 0, earnedComm: 0 }
      );

      return {
        ...g,
        totalVolumes: totals.totalVolume,
        totalCommissionVolumes: totals.totalVolume,
        earnedCommission: totals.earnedComm,
      };
    });
  }, [invoices, clientBrokerMap, effectiveDateMap, clients, toNumber]);

  const handleBackClicked = () => {
    navigate('/manage-brokers/list'); // Navigate to /invoice page
  };

  // Extend dayjs with plugins
  dayjs.extend(timezone);
  dayjs.extend(utc);

  const handleStartDateChange = date => {
    console.log('Original Start Date:', date);

    // Convert the selected date to CST (Central Standard Time)
    const dateInCST = dayjs(date).tz('America/Chicago', true); // CST timezone conversion

    console.log('Start Date in CST:', dateInCST.format()); // Log the date in CST

    // Set the state with the CST formatted date
    setStartDate(dateInCST);

    // Reset error text
    setStartDateErrorText('');
  };

  const handleEndDateChange = date => {
    console.log('Original End Date:', date);

    // Convert the selected date to CST (Central Standard Time)
    const dateInCST = dayjs(date).tz('America/Chicago', true); // CST timezone conversion

    console.log('End Date in CST:', dateInCST.format()); // Log the date in CST

    // Set the state with the CST formatted date
    setEndDate(dateInCST);

    // Reset error text
    setEndDateErrorText('');
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(clientId);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'client-select-popover' : undefined;

  const [selectedBroker, setSelectedBroker] = React.useState(''); // '' | 'all' | broker.id
  const [brokerSearchTerm, setBrokerSearchTerm] = React.useState(''); // search box
  const filteredBrokers = React.useMemo(() => brokers
 .filter(b => `${b.id} ${b.brokerName}`.toLowerCase().includes(brokerSearchTerm.toLowerCase()))
 .sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10)), 
  [brokers, brokerSearchTerm]);

  const handleBrokerSelect = id => {
    setSelectedBroker(id);
    setAnchorEl(null); // close the popover
    // ⬇︎ if you need to notify parent logic, call a prop or dispatch here
    //   onBrokerChange?.(id);
  };

  const brokerLabel = () => {
    if (selectedBroker === 'all') return 'All Brokers';
    if (!selectedBroker) return 'Broker';
    const b = brokers.find(x => x.id === selectedBroker);
    return b ? `${b.id} - ${b.brokerName}` : 'Broker';
  };

  return (
    <div>
      <>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography
            variant="h1"
            style={{
              textAlign: 'left',
              fontFamily: 'sans-serif',
              fontWeight: 'bold',
              fontSize: '14px',
              marginLeft: '17px',
            }}
            gutterBottom
          >
            BROKER COMMISSION REPORT
          </Typography>
          <div style={{ display: 'flex', marginBottom: '10px' }}>
            <Button
              variant="contained"
              style={{
                fontFamily: 'sans-serif',
                fontSize: '10px',
                backgroundColor: '#31478F',
                padding: '4px 9px', // Reduce padding
                minWidth: '70px', // Reduce width
                height: '24px', // Reduce height
                marginRight: '20px',
              }}
              onClick={handleBackClicked}
            >
              BACK
            </Button>
          </div>
        </div>
        <div>
          <form>
            <div className="container" style={{ display: 'flex' }}>
              <div className="card" style={{ flex: '1' }}>
                {/* <Card sx={{ minWidth: "100%", minHeight: cardHeight }}> */}
                <CardContent>
                  {/* // <form id="transaction-report" onSubmit={handleViewReportCAD}> */}
                  <Grid container spacing={2} style={gridRowStyles}>


                    <Grid item xs={4} sm={4} md={4} lg={4}>
                      <Box>
                        <FormControl sx={{ minWidth: '100%', minHeight: 2 }} size="small" error={customerErrorText}>
                          {/* ▸▸▸ When Super Admin (show dropdown) or Broker (show logged-in broker only) ◂◂◂ */}
                          {doNotPerformCustomerFiltration ? (
                            <div>
                              <IconButton
                                aria-describedby={id}
                                onClick={handleClick}
                                endIcon={<ArrowDropDownIcon />}
                                sx={{
                                  textAlign: 'left',
                                  width: '100%',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  padding: '4.5px',
                                  boxSizing: 'border-box',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  minHeight: '10px',
                                }}
                                style={inputLabelStyles}
                              >
                                {brokerLabel()}
                              </IconButton>
                              <Popover id={id} anchorEl={anchorEl} open={open} onClose={handleClose} PaperProps={{ style: { maxHeight: 250, width: 300 } }}>
                                <div style={{ width: '95%' }}>
                                  <TextField
                                    autoFocus
                                    margin="dense"
                                    id="search"
                                    label="Search"
                                    type="search"
                                    fullWidth
                                    variant="outlined"
                                    onChange={e => setBrokerSearchTerm(e.target.value)}
                                    sx={{
                                      margin: 1,
                                      '& .MuiInputBase-root': { height: '25px', fontSize: '10px', padding: '2px 6px' },
                                      '& .MuiInputLabel-root': { fontSize: '10px' },
                                    }}
                                  />
                                  <MenuItem value="all" onClick={() => handleBrokerSelect('all')} sx={{ fontSize: '12px', padding: '4px 8px' }}>
                                    All Brokers
                                  </MenuItem>
                                  {filteredBrokers.length ? (
                                    filteredBrokers.map(b => (
                                      <MenuItem key={b.id} selected={b.id === selectedBroker} onClick={() => handleBrokerSelect(b.id)} sx={{ fontSize: '12px', padding: '4px 8px' }}>
                                        {b.id} - {b.brokerName}
                                      </MenuItem>
                                    ))
                                  ) : (
                                    <MenuItem disabled>No results found</MenuItem>
                                  )}
                                </div>
                              </Popover>
                            </div>
                          ) : (
                            /* ▸▸▸ When Broker role (show logged-in broker only) ◂◂◂ */
                            <div style={{ marginTop: 2, marginLeft: 16 }}>
                              <Typography variant="body1">
                                {brokers.length > 0 ? `${brokers[0].id} - ${brokers[0].brokerName}` : 'No Broker Available'}
                              </Typography>
                            </div>
                          )}
                          {customerErrorText && <FormHelperText id="customer-error-text">{customerErrorText}</FormHelperText>}
                        </FormControl>
                      </Box>
                    </Grid>
                    <Grid item xs={4} sm={4} md={4} lg={4}>
                      <Box>
                        <FormControl sx={{ minWidth: '100%' }} size="small" error={startDateErrorText}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              label="Start Date"
                              value={startDate}
                              InputLabelProps={textFieldLabelStyles}
                              onChange={handleStartDateChange}
                              shouldDisableDate={date => {
                                const day = date.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
                                const today = dayjs().startOf('day'); // Get today's date (without time)
                                return (day !== 1 && day !== 4) || date.isAfter(today);
                              }}
                              slotProps={{
                                textField: {
                                  sx: {
                                    '& .MuiInputLabel-root': {
                                      fontSize: '12px', // Adjust font size

                                      top: '-10px',
                                    },
                                    '& .MuiInputBase-root': {
                                      height: 25,
                                      minHeight: 25,
                                    },
                                    '& .MuiInputBase-input': {
                                      fontSize: 11,
                                      padding: '2px',
                                    },
                                    '& .MuiSvgIcon-root': {
                                      fontSize: '15px', // Reduce icon size
                                    },
                                  },
                                },
                                popper: {
                                  modifiers: [
                                    {
                                      name: 'preventOverflow',
                                      options: {
                                        boundary: 'window',
                                      },
                                    },
                                  ],
                                },
                              }}
                              componentsProps={{
                                popper: {
                                  sx: {
                                    '& .MuiPaper-root': {
                                      maxHeight: '200px',
                                      minHeight: '100px',
                                      minWidth: '120px', // Reduce dropdown width
                                      maxWidth: '150px',
                                    },
                                    '& .MuiMenuItem-root': {
                                      fontSize: '10px',
                                      marginLeft: '5px',
                                      padding: '3px 12px',
                                    },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                          {startDateErrorText && <FormHelperText id="input-error-text">{startDateErrorText}</FormHelperText>}
                        </FormControl>
                      </Box>
                    </Grid>
                    <Grid item xs={4} sm={4} md={4} lg={4}>
                      <Box>
                        <FormControl sx={{ minWidth: '100%' }} size="small" error={endDateErrorText}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              label="End Date"
                              value={endDate}
                              InputLabelProps={textFieldLabelStyles}
                              onChange={handleEndDateChange}
                              shouldDisableDate={date => {
                                const day = date.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
                                const today = dayjs().startOf('day'); // Get today's date (without time)
                                return (day !== 0 && day !== 3) || date.isAfter(today);
                              }}
                              slotProps={{
                                textField: {
                                  sx: {
                                    '& .MuiInputLabel-root': {
                                      fontSize: '12px', // Adjust font size

                                      top: '-10px',
                                    },
                                    '& .MuiInputBase-root': {
                                      height: 25,
                                      minHeight: 25,
                                    },
                                    '& .MuiInputBase-input': {
                                      fontSize: 11,
                                      padding: '2px',
                                    },
                                    '& .MuiSvgIcon-root': {
                                      fontSize: '15px', // Reduce icon size
                                    },
                                  },
                                },
                              }}
                              componentsProps={{
                                popper: {
                                  sx: {
                                    '& .MuiPaper-root': {
                                      maxHeight: '200px',
                                      minHeight: '100px',
                                      minWidth: '120px', // Reduce dropdown width
                                      maxWidth: '150px',
                                    },
                                    '& .MuiMenuItem-root': {
                                      fontSize: '10px',
                                      marginLeft: '5px',
                                      padding: '3px 12px',
                                    },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                          {endDateErrorText && <FormHelperText id="input-error-text">{endDateErrorText}</FormHelperText>}
                        </FormControl>
                      </Box>
                    </Grid>
                    <Grid item xs={4} sm={4} md={4} lg={4} align="left">
                      <Button
                        style={{
                          fontFamily: 'sans-serif',
                          fontSize: '10px',
                          backgroundColor: '#31478F',
                          padding: '4px 9px', // Reduce padding
                          minWidth: '70px', // Reduce width
                          height: '24px', // Reduce height
                          marginLeft: '400px',
                          marginTop: '-4px',
                          whiteSpace: 'nowrap',
                        }}
                        type="submit"
                        variant="contained"
                        disabled={uploading} // Disable when uploading
                        sx={{ mt: 2, mb: 5, borderRadius: '5px' }}
                        onClick={fetchInvoices}
                      >
                        {uploading ? <CircularProgress size={24} color="inherit" /> : 'Fetch Commission Report'}
                      </Button>
                    </Grid>
                  </Grid>
                  {/* {loading && <Loader />} */}
                </CardContent>
                {/* </Card> */}
              </div>
            </div>
          </form>
        </div>

        {invoices.length > 0 ? (
          <Box>

            <TableToolbar onExport={() => exportBrokerDataToExcel(brokerRows, getCommissionRate, toNumber, clients)} />
            <BrokerClientTable brokerRows={brokerRows} commissionLookup={(clientId, invoiceEndDate) => getCommissionRate(clientId, invoiceEndDate, clients, toNumber)} toNumber={toNumber} effectiveDateMap={effectiveDateMap} />          </Box>
        ) : (
          fetchedWithNoRecords && (
            <TableContainer component={Paper} sx={{ width: '97%', marginLeft: '20px', marginTop: '-25px' }}>
              <Table aria-label="collapsible table" size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#31478F' }}>
                    <TableCell sx={{ color: 'white', fontSize: '10px', textAlign: 'center' }} />
                    <TableCell sx={{ color: 'white', fontSize: '10px', textAlign: 'center' }}>BROKER ID</TableCell>
                    <TableCell sx={{ color: 'white', fontSize: '10px', textAlign: 'center' }}>BROKER NAME</TableCell>
                    <TableCell sx={{ color: 'white', fontSize: '10px', textAlign: 'center' }}>INVOICE PERIOD</TableCell>
                    <TableCell sx={{ color: 'white', fontSize: '10px', textAlign: 'center' }}>TOTAL VOLUMES</TableCell>
                    <TableCell sx={{ color: 'white', fontSize: '10px', textAlign: 'center' }}>TOTAL COMMISSION VOLUMES</TableCell>
                    <TableCell sx={{ color: 'white', fontSize: '10px', textAlign: 'center' }}>EARNED COMMISSION</TableCell>
                    <TableCell sx={{ color: 'white', fontSize: '10px', textAlign: 'center' }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{ backgroundColor: '#F8F8F8' }}>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell sx={{ fontSize: '10px', textAlign: 'center' }}>No Invoice records found</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
      </>
      {/* )} */}
    </div>
  );
}

export default BrokerReport;

export function BrokerClientTable({ brokerRows, commissionLookup, toNumber, effectiveDateMap }) {
  const [openById, setOpenById] = useState({}); // brokerId -> boolean

  const toggle = id => setOpenById(p => ({ ...p, [id]: !p[id] }));

  const nf = (n, opts = {}) =>
    (n ?? 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...opts,
    });

  return (
    <Table size="small" sx={{ width: '97%', borderCollapse: 'collapse' }}>
      {/* ── HEADER ───────────────────────────────────────────── */}
      <TableHead>
        <TableRow sx={{ backgroundColor: '#31478F' }}>
          <TableCell sx={{ width: 50 }} />
          <TableCell sx={{ color: 'white', fontSize: 10, textAlign: 'left' }}>BROKER&nbsp;ID</TableCell>
          <TableCell sx={{ color: 'white', fontSize: 10, textAlign: 'left' }}>BROKER&nbsp;NAME</TableCell>
          {/* <TableCell sx={{ color: 'white', fontSize: 10, textAlign: 'center' }}>INV&nbsp;PERIOD</TableCell> */}
          <TableCell sx={{ color: 'white', fontSize: 10, textAlign: 'left' }}>TOTAL&nbsp;VOLUMES</TableCell>
          <TableCell sx={{ color: 'white', fontSize: 10, textAlign: 'left' }}>TOTAL&nbsp;COMM&nbsp;VOLUMES</TableCell>
          <TableCell sx={{ color: 'white', fontSize: 10, textAlign: 'left' }}>EARNED&nbsp;COMMISSION</TableCell>
        </TableRow>
      </TableHead>

      {/* ── BODY ─────────────────────────────────────────────── */}
      <TableBody>
        {brokerRows.map((broker, idx) => {
          const isOpen = openById[broker.id];
          const rowBg = idx % 2 === 0 ? '#F8F8F8' : '#E6F0FF';

          return (
            <React.Fragment key={broker.id}>
              {/* ─ Broker Row ─ */}
              <TableRow sx={{ backgroundColor: rowBg }}>
                <TableCell padding="checkbox">
                  <IconButton size="small" onClick={() => toggle(broker.id)}>
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </TableCell>
                <TableCell sx={{ fontSize: 10, fontWeight: 600, textAlign: 'left' }}>{broker.brokerId}</TableCell>
                <TableCell sx={{ fontSize: 10, fontWeight: 600, textAlign: 'left', textTransform: 'uppercase' }}>{broker.brokerName}</TableCell>
                {/* <TableCell sx={{ fontSize: 10 }} /> */}
                <TableCell sx={{ fontSize: 10, textAlign: 'left' }}>{nf(broker.totalVolumes)}</TableCell>
                <TableCell sx={{ fontSize: 10, textAlign: 'left' }}>{nf(broker.totalCommissionVolumes)}</TableCell>
                <TableCell sx={{ fontSize: 10, textAlign: 'left' }}>{nf(broker.earnedCommission, { style: 'currency', currency: 'USD' })}</TableCell>
              </TableRow>

              {/* ─ Client block (collapse) ─ */}
              <TableRow>
                <TableCell colSpan={7} sx={{ padding: 0, backgroundColor: '#fafafa' }}>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    {/* inner table for clients */}
                    <Box sx={{ ml: 4, my: 1 }}>
                      {broker.clients.length === 0 ? (
                        <Typography sx={{ fontSize: 10, fontStyle: 'italic', p: 1 }}>No client invoices</Typography>
                      ) : (
                        <Table size="small" sx={{ width: '100%' }}>
                          {/* ── HEADER ───────────────────────────────────────────── */}
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#31478F' }}>
                              {/* <TableCell sx={{ width: 50 }} /> */}
                              <TableCell sx={{ color: 'white', fontSize: 10, textAlign: 'left' }}>CLIENT&nbsp;ID</TableCell>
                              <TableCell sx={{ color: 'white', fontSize: 10, textAlign: 'left' }}>CLIENT&nbsp;NAME</TableCell>
                              <TableCell sx={{ color: 'white', fontSize: 10, textAlign: 'left' }}>INV&nbsp;PERIOD</TableCell>
                              <TableCell sx={{ color: 'white', fontSize: 10, textAlign: 'left' }}>TOTAL&nbsp;VOLUMES</TableCell>
                              <TableCell sx={{ color: 'white', fontSize: 10, textAlign: 'left' }}>TOTAL&nbsp;COMM&nbsp;VOLUMES</TableCell>
                              <TableCell sx={{ color: 'white', fontSize: 10, textAlign: 'left' }}>EARNED&nbsp;COMMISSION</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {broker.clients
                              .filter(c => {
                                const eff = effectiveDateMap?.[String(c.clientId)];
                                return !eff || c.invoiceEndDate >= eff;
                              })
                              .sort((a, b) => {
                                const getStartDate = invoiceId => {
                                  const parts = invoiceId.split('_');
                                  return dayjs(parts[1], 'MMMDDYYYY');
                                };
                                return getStartDate(a.invoiceId).isBefore(getStartDate(b.invoiceId)) ? -1 : 1;
                              })
                              .map((c, cIdx) => {
                                const diesel = toNumber(c.totalDiesel);
                                const rfr = toNumber(c.totalRfr);
                                const vol = diesel + rfr;
                                // Modified: Use getCommissionRate with invoiceEndDate
                                const rate = commissionLookup(c.clientId, c.invoiceEndDate);
                                const commission = vol * rate;

                                return (
                                  <TableRow key={`cli-${c.clientId}-${c.invoiceId}`} sx={{ backgroundColor: cIdx % 2 ? '#ffffff' : '#f2f2f2' }}>
                                    <TableCell sx={{ fontSize: 9, textAlign: 'left' }}>{c.clientId}</TableCell>
                                    <TableCell sx={{ fontSize: 9, textAlign: 'left', textTransform: 'uppercase' }}>{c.customerName}</TableCell>
                                    <TableCell sx={{ fontSize: 9, textAlign: 'left', textTransform: 'uppercase' }}>{c.invoiceId}</TableCell>
                                    <TableCell sx={{ fontSize: 9, textAlign: 'left' }}>{nf(vol)}</TableCell>
                                    <TableCell sx={{ fontSize: 9, textAlign: 'left' }}>{nf(vol)}</TableCell>
                                    <TableCell sx={{ fontSize: 9, textAlign: 'left' }}>{nf(commission, { style: 'currency', currency: 'USD' })}</TableCell>
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                      )}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}